import {MDIntent} from '@mintdata-wsp/common/components/MDIntent';
import {ApiError} from '@mintdata-wsp/common/controllers/network/Fetcher';
import {ModifyProjectRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/ModifyProjectRequest';
import {ProjectResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/ProjectResponse';
import {API} from '@mintdata-wsp/common/controllers/network/WorkspaceAPI';
import {Project} from '@mintdata-wsp/common/state/DataState';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {ErrorSubMessages} from '@mintdata-wsp/common/state/ui/visualDesigner/ToastData';
import {Dialogs} from '@mintdata-wsp/wsp/controllers/common/DialogsController';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {TextInputDialogProps} from '@mintdata-wsp/wsp/ui/dialogs/TextInputDialog';
import bind from 'bind-decorator';
import {action} from 'mobx';

import * as React from 'react';

export class ProjectsController
{
    public init():void
    {
        if( controllers.user.isLoggedIn() )
            this.syncAllProjects();
    }

    public syncAllProjects():Promise<any>
    {
        return this.getAllProjects()
            .then(action((allProjects:Project[]) =>
            {
                rootState.data.projects = allProjects;
            }));
    }

    public getProject(id:number):Promise<Project>
    {
        if( rootState.userSession )
            return API.projects.get(id).then(response => Project.fromResponse(response));

        return Promise.reject(null);
    }

    public async getAllProjects():Promise<Project[]>
    {
        if( rootState.userSession )
            return this.getOrganizationProjects(rootState.userSession.currentOrganization.siteId);

        return null;
    }

    public async getOrganizationProjects(organisationSid:string):Promise<Project[]>
    {
        return API.organizations.projectsList(organisationSid).then(response =>
            this._projectResponsesToProjects(response.projects)
        );
    }

    private _projectResponsesToProjects(projectResponses:ProjectResponse[]):Project[]
    {
        if( !projectResponses )
            throw {
                message: 'Ops!',
                subMessage: ErrorSubMessages.Support
            };

        return projectResponses.map(projectResponse => Project.fromResponse(projectResponse));
    }

    public createProject(name:string, description:string):Promise<Project>
    {
        return new Promise((resolve, reject) =>
        {
            if( rootState.userSession )
            {
                const req = new ModifyProjectRequest(name, description);

                API.projects.create(req).then(action((response:ProjectResponse) =>
                {
                    if( ApiError.isError(response) )
                    {
                        reject(response);
                        return;
                    }

                    const newProject = Project.fromResponse(response);
                    rootState.data.projects.push(newProject);

                    resolve(newProject);
                }));
            }
            else
                reject(null);
        });
    }

    public updateProject(project:Project, name:string, description:string):Promise<Project>
    {
        return new Promise((resolve, reject) =>
        {
            if( rootState.userSession )
            {
                const req = new ModifyProjectRequest(name, description);

                API.projects.update(project.id, req)
                    .then(action((response:any) =>
                    {
                        if( ApiError.isError(response) )
                        {
                            reject(response as ApiError);
                            return;
                        }

                        project.name = name;
                        project.description = description;

                        resolve(project);
                    }));
            }
            else
                reject(null);
        });
    }

    @bind
    public deleteProject(project:Project):Promise<any>
    {
        return new Promise((resolve, reject) =>
        {
            if( rootState.userSession )
            {
                API.projects.delete(project.id)
                    .then(action((response) =>
                    {
                        if (response instanceof ApiError)
                        {
                            throw new Error(response.message);
                        }

                        const {projects} = rootState.data;
                        const index = projects.indexOf(project);

                        projects.splice(index, 1);

                        resolve(project);
                    }));
            }
            else
                reject(null);
        });
    }

    @bind
    public renameProjectDialog(project:Project):void
    {
        const props = new TextInputDialogProps(
            MDIntent.Default,
            `Rename space "${project.name}"`,
            null,
            null,
            'Enter new name',
            project.name,
            null,
            [null, 'Rename space'],
            this._onConfirmedRenameProject(project),
            null,
            true,
            this._onChangeRenameProject,
            this._getErrorMessage
        );

        this._setRenameSpaceServerError(null);
        controllers.ui.dialogs.showDialog(Dialogs.TextInput, false, props);
    }

    public deleteSpaceDialog(project:Project):Promise<void>
    {
        return new Promise((resolve, reject) =>
        {
            const designCount:number = project.numOfDesigns || 0;
            const appCount:number = rootState.data.publishedApps.filter(app => app.projectId === project.id).length || 0;

            const contentLines = [];
            const e = React.createElement;

            if( appCount )
                contentLines.push(
                    e('span', {key:'line1'}, `All applications (${appCount}) in this space will be unpublished.`),
                    e('br', {key:'br1'})
                );

            if( designCount )
                contentLines.push(
                    e('span', {key:'line2'}, `All designs (${designCount}) in this space will be deleted.`),
                    e('br', {key:'br2'})
                );
            
            contentLines.push(e('span', {key:'line3'}, 'This operation cannot be undone.'));

            const content:JSX.Element = e('span', null, contentLines);

            controllers.ui.dialogs.showDangerConfirm(
                `Delete space "${project && project.name}"?`,
                content,
                async () =>
                {
                    await this.deleteProject(project)
                        .then(() => resolve())
                        .catch((error) => reject(error));
                },
                [null, 'Delete space']
            );
        });
    }

    private _getErrorMessage():string
    {
        return rootState.ui.mainPage.renameSpace.errorMessage;
    }

    @bind
    private _onChangeRenameProject(newValue:string)
    {
        this._setRenameSpaceServerError(null);
    }

    @action
    private _setRenameSpaceServerError(errorMessage:string)
    {
        rootState.ui.mainPage.renameSpace.errorMessage = errorMessage;
    }

    private _onConfirmedRenameProject(project:Project)
    {
        return (newInputValue:string) =>
        {
            if( !newInputValue ) return false;

            this._setRenameSpaceServerError(null);

            controllers.data.projects
                .updateProject(project, newInputValue.trim(), '')
                .then(() =>
                {
                    controllers.ui.dialogs.hideDialog(Dialogs.TextInput);
                })
                .catch((error:ApiError) =>
                {
                    this._setRenameSpaceServerError(error.message);
                    // throw new Error(error.message);
                });

            return false; // don't hide TextInput Dialog
        };
    }
}
